import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Link, Loading, Tab, TabList, Tabs } from "@carbon/react";
import { CompanyProfileResponse } from "../../types";
import { useSupplier } from "../../hooks/useSuppliers";
import CompanyLogo from "../../components/CompanyProfile/CompanyLogo";
import useID from "../../hooks/useID";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import OverviewTab from "../CompanyProfile/tabs/OverviewTab";
import CompanyDataTab from "../CompanyProfile/tabs/CompanyDataTab";
import ContactsTab from "../CompanyProfile/tabs/ContactsTab";
import MainClientsTab from "../CompanyProfile/tabs/MainClientsTab";
import "../CompanyProfile/companyProfile.scss";
import "./supplier.scss";

/**
 * The supplier DETAIL viewer (`/suppliers/:id`), a buyer looking at ANOTHER company, rebuilt on
 * Carbon in place of ~637 lines of Tailwind. It is the read-only sibling of `CompanyProfile.tsx`
 * (the seller's own editable `/company` viewer, Task 6 of the Profile/Company flow): both render
 * the exact same company graph across the same four tabs, so this container REUSES that flow's
 * shared, purely-presentational tab components (`../CompanyProfile/tabs/*`) rather than carrying
 * its own now-deleted inline duplicates. The tabs are imported where they live — relocating them
 * would ripple into the already-done Profile/Company flow.
 *
 * Buyer-only gating is the route tree's job: `/suppliers/:id` is mounted only on the buyer role
 * trees (Admin/Manager/Buyer/Analyst) — there is no seller/`isSeller` check here, and none is
 * added. Unlike `CompanyProfile.tsx` this mounts no `CompletionPercentage` and offers no
 * edit/create action: a buyer cannot edit another company.
 *
 * DTO note (Task 2 leaned the backend `SupplierResponse`; Task 3 reconciled the FE type): the
 * endpoint carries the tax id as `cuit`, not the inherited `taxId` the shared tabs read, so it is
 * bridged onto `companyProfile` below (`taxId ← cuit`, `name ← commercialName`). Everything else
 * on the leaned DTO maps by its real name, and the shared tabs are already defensive (`?? []`,
 * optional chaining) against any field the DTO omits.
 */
const Supplier = () => {
  const id = useID();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const { data: supplier, isLoading } = useSupplier(id);

  if (isLoading) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  if (!supplier) {
    return (
      <EmptyState
        title={t("suppliers.notFound")}
        action={
          <Button onClick={() => navigate("/suppliers")}>
            {t("cta.goBack")}
          </Button>
        }
      />
    );
  }

  // The leaned supplier DTO exposes the tax id as `cuit`; the shared company-graph tabs read the
  // inherited `taxId`. Bridge it here so the reused tabs render the supplier's real value,
  // defensively falling back only when the field is present.
  const companyProfile: CompanyProfileResponse = {
    ...supplier,
    taxId: supplier.taxId ?? supplier.cuit,
  };

  return (
    <div>
      <header className="supplier__header">
        <CompanyLogo fileKey={supplier.imageURL} name={supplier.commercialName} />
        <h1 className="supplier__header-name">{supplier.commercialName}</h1>
        {(supplier.website || supplier.linkedinUrl) && (
          <div className="supplier__header-links">
            {supplier.website && (
              <Link href={supplier.website} target="_blank" rel="noopener noreferrer">
                {t("companyProfile.website")}
              </Link>
            )}
            {supplier.linkedinUrl && (
              <Link
                href={supplier.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("companyProfile.linkedin")}
              </Link>
            )}
          </div>
        )}
      </header>

      <div className="company-profile__tabs">
        <Tabs
          selectedIndex={selectedTab}
          onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
        >
          <TabList aria-label={supplier.commercialName}>
            <Tab>{t("companyProfile.basicInformation")}</Tab>
            <Tab>{t("companyProfile.companyData")}</Tab>
            <Tab>{t("companyProfile.contacts")}</Tab>
            <Tab>{t("companyProfile.mainClients")}</Tab>
          </TabList>
        </Tabs>
      </div>

      <div className="company-profile__content">
        {selectedTab === 0 && <OverviewTab companyProfile={companyProfile} />}
        {selectedTab === 1 && <CompanyDataTab companyProfile={companyProfile} />}
        {selectedTab === 2 && <ContactsTab companyProfile={companyProfile} />}
        {selectedTab === 3 && <MainClientsTab companyProfile={companyProfile} />}
      </div>
    </div>
  );
};

export default Supplier;
